import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, TextField, Typography } from "@mui/material";
import { MouseEventHandler, use, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { UidNumber } from "tng-address-collection-shared/types/primitives.ts";
import { unknownEmployee } from "tng-address-collection-shared/unknownEmployee.ts";
import { z } from "zod/v4";
import { useGetCardSignatures } from "../hooks/useGetCardSignatures.ts";
import { useGetEmployees } from "../hooks/useGetEmployees.ts";
import { useToggleOtherCardSignature } from "../hooks/useToggleOtherCardSignature.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { EmployeesMultiSelectionFormField } from "./EmployeesMultiSelectionFormField.tsx";
import { FormContainer } from "./FormContainer.tsx";

const EditSignaturesFormContent = z.object({
  signaturesToAdd: UidNumber.array(),
  signaturesToRemove: UidNumber.array(),
});
type EditSignaturesFormContent = z.infer<typeof EditSignaturesFormContent>;

const defaultValues: EditSignaturesFormContent = {
  signaturesToAdd: [],
  signaturesToRemove: [],
};

export const EditCardSignaturesDialog = ({
  cardId,
  projectId,
  canRemoveOtherSignatures,
  onClose,
}: {
  cardId: string;
  projectId: string;
  canRemoveOtherSignatures: boolean;
  onClose: () => void;
}) => {
  const translator = useTranslator();
  const { displayError, displaySuccess } = use(SnackbarContext);

  const { data: employees } = useGetEmployees();
  const { data } = useGetCardSignatures(cardId, projectId);
  const { mutateAsync, isPending } = useToggleOtherCardSignature(
    cardId,
    projectId,
  );

  const { optionsToAdd, optionsToRemove } = useMemo(
    () => ({
      optionsToAdd:
        employees?.filter(({ uidNumber }) =>
          data?.signatures.every(
            (signature) => uidNumber !== signature.uidNumber,
          ),
        ) ?? [],
      optionsToRemove:
        data?.signatures?.map(
          ({ uidNumber }) =>
            employees?.find((employee) => employee.uidNumber === uidNumber) ??
            unknownEmployee(uidNumber),
        ) ?? [],
    }),
    [data?.signatures, employees],
  );

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(EditSignaturesFormContent),
    defaultValues,
  });

  useEffect(() => {
    reset();
  }, [cardId, reset]);

  const onSubmit = (formData: EditSignaturesFormContent) => {
    Promise.all([
      ...formData.signaturesToAdd.map((uidNumber) =>
        mutateAsync({ uidNumber, action: "add" }),
      ),
      ...formData.signaturesToRemove.map((uidNumber) =>
        mutateAsync({ uidNumber, action: "remove" }),
      ),
    ])
      .then(() => {
        displaySuccess(translator("changesSaved"));
        onClose();
        return;
      })
      .catch(() => {
        displayError(translator("errorSavingChanges"));
      });
  };

  return (
    <FormContainer
      title={translator("editCardSignatures")}
      onClose={onClose}
      isLoading={!data}
      saveConfig={{
        onSave: handleSubmit(onSubmit) as MouseEventHandler,
        isSaveDisabled: isPending,
      }}
    >
      <Stack direction="row" spacing={1}>
        <TextField
          label={translator("firstName")}
          value={data?.firstName ?? ""}
          disabled
          variant="outlined"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label={translator("lastName")}
          value={data?.lastName ?? ""}
          disabled
          variant="outlined"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      <Typography variant="h6">{translator("addCardSignatures")}</Typography>
      <EmployeesMultiSelectionFormField
        control={control}
        name="signaturesToAdd"
        label={translator("cardSignaturesToAdd")}
        options={optionsToAdd}
      />

      {canRemoveOtherSignatures && (
        <>
          <Typography variant="h6">
            {translator("removeCardSignatures")}
          </Typography>
          <EmployeesMultiSelectionFormField
            control={control}
            name="signaturesToRemove"
            label={translator("cardSignaturesToRemove")}
            options={optionsToRemove}
          />
        </>
      )}
    </FormContainer>
  );
};
